/* use this module for shared components, pipes and directives, per https://v7.angular.io/guide/styleguide#shared-feature-module;
 * do NOT put shared services in this module - use CoreModule instead */
import { CommonModule } from '@angular/common';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// angular material
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatLegacyProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatToolbarModule } from '@angular/material/toolbar';

// kendo
import { ButtonModule, ButtonsModule } from '@progress/kendo-angular-buttons';
import { DateInputsModule, TimePickerModule } from '@progress/kendo-angular-dateinputs';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { EditorModule } from '@progress/kendo-angular-editor';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { PopupModule } from '@progress/kendo-angular-popup';
import { SchedulerModule } from '@progress/kendo-angular-scheduler';
import { ToolBarModule } from '@progress/kendo-angular-toolbar';
import { TooltipModule } from '@progress/kendo-angular-tooltip';

// services
import { SpinnerComponent } from './services/full-screen-loading.service';
import { MessageDialogComponent } from './services/message-dialog.service';

// shared components
import { FileUploadModule } from 'ng2-file-upload';
import { AddPerDiemFollowupPickerComponent } from 'src/app/shared/components/add-perdiem-followup-picker/add-perdiem-followup-picker.component';
import { EmailContainer } from 'src/app/shared/components/email-container/email-container.component';
import { FollowupPickerPerdiemComponent } from 'src/app/shared/components/followup-picker-perdiem/followup-picker-perdiem.component';
import { FollowupPickerComponent } from 'src/app/shared/components/followup-picker/followup-picker.component';
import { PerdiemRecruiterResourcesComponent } from 'src/app/shared/components/perdiem-recruiter-resources/perdiem-recruiter-resources.component';
import { BillingRatesComponent } from '../facility-contracts/facilities/details/components/billing-rates/billing-rates.component';
import { ExpansionPanelComponent } from './components/expansion-panel/expansion-panel.component';
import { GenericDialogComponent } from './components/generic-dialog/generic-dialog.component';
import { JobFulfillmentTypeSelectorConfirmationComponent } from './components/job-fulfillment-type-selector/job-fulfillment-type-selector-confirmation.component';
import { JobFulfillmentTypeSelectorComponent } from './components/job-fulfillment-type-selector/job-fulfillment-type-selector.component';
import { KendoMultipleDatePickerComponent } from './components/kendo-multiple-date-picker/kendo-multiple-date-picker.component';
import { LeadScoreProfileRatingComponent } from './components/lead-score-profile-rating/lead-score-profile-rating.component';
import { NoteDialogComponent } from './components/note-dialog/note-dialog.component';
import { RecruiterBonusComponent } from './components/recruiter-bonus/recruiter-bonus.component';
import { DateDialogComponent } from './date-dialog.component';
import { DropDownDialogComponent } from './drop-down-dialog.component';
import { MoreOptionsMenuComponent } from './more-options-menu.component';
import { PaginationComponent } from './pagination.component';

// pipes
import { FullscreenOverlayContainer, OverlayContainer, OverlayModule } from '@angular/cdk/overlay';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTreeModule } from '@angular/material/tree';
import { RouterModule } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import { SearchTemplateComponent } from 'src/app/esign/search-template/search-template.component';
import { TemplateFolderListComponent } from 'src/app/esign/template-folder-list/template-folder-list.component';
import { TemplateGridComponent } from 'src/app/esign/template-grid/template-grid.component';
import { TemplateMappingFlyOutComponent } from 'src/app/esign/template-mapping-fly-out/template-mapping-fly-out.component';
import { BillingInvoicingComponent } from 'src/app/facility-contracts/facilities/details/components/billing-invoicing/billing-invoicing.component';
import { AutocompleteMultiselectComponent } from 'src/app/shared/components/autocomplete-multiselect/autocomplete-multiselect.component';
import { CreateTicketMenuComponent } from 'src/app/shared/components/create-ticket-menu/create-ticket-menu.component';
import { CardComponent } from 'src/app/shared/components/nova-card/nova-card.component';
import { VaccinePreviewComponent } from 'src/app/shared/components/vaccine-preview/vaccine-preview.component';
import { EventMetricsDirective } from 'src/app/shared/directives/event-metrics.directive';
import { MatInputCommifiedDirective } from 'src/app/shared/directives/mat-input-commified.directive';
import { ConfirmationDialogEffects } from 'src/app/shared/effects/confirmation-dialog.effects';
import { ContractAssignmentsEffects } from 'src/app/shared/effects/contract-assignments.effects';
import { ToasterEffects } from 'src/app/shared/effects/toaster.effects';
import { EmptyArrayPipe } from 'src/app/shared/pipes/empty-array.pipe';
import { GroupByPipe } from 'src/app/shared/pipes/group-by.pipe';
import { IsEmptyPipe } from 'src/app/shared/pipes/is-empty.pipe';
import { JoinTextPipe } from 'src/app/shared/pipes/join-text.pipe';
import { LinkifyPipe } from 'src/app/shared/pipes/linkify.pipe';
import { OrderByPipe } from 'src/app/shared/pipes/order-by.pipe';
import { PayRangeTextPipe } from 'src/app/shared/pipes/pay-range-text.pipe';
import { SafedomPipe } from 'src/app/shared/pipes/safedom.pipe';
import { TextFormatPipe } from 'src/app/shared/pipes/text-format.pipe';
import { sharedReducers } from 'src/app/shared/reducers';
import { WizardStepComponent } from 'src/app/shared/wizard-step/wizard-step.component';
import { BillingInfoComponent } from 'src/app/travel-contracts/travel-contracts-grid/details/facility-billing-details/billing-info/billing-info.component';
import { BillingProfileComponent } from 'src/app/travel-contracts/travel-contracts-grid/details/facility-billing-details/billing-profile/billing-profile.component';
import { AssignmentLevelTypePipe } from '../credentialing/prestart/pipes/assignment-level-type.pipe';
import { ImportJobRequirementsComponent } from '../facility-contracts/facilities/details/components/facility-profile/dialogs/import-job-requirements/import-job-requirements.component';
import { V1NotesComponent } from '../recruiting/candidates/new-profile/about/v1-notes/v1-notes.component';
import { WizardComponent } from '../shared/wizard/wizard.component';
import { AccessControlInformationViewEditComponent } from './components/access-control-information-view-edit/access-control-information-view-edit.component';
import { AtmButtonComponent } from './components/atm-button/atm-button.component';
import { AutocompleteSelectAllMultiselectComponent } from './components/autocomplete-select-all-multiselect/autocomplete-select-all-multiselect.component';
import { CalendarComponent } from './components/calendar/calendar.component';
import { CommentWithAttachmentsDialogComponent } from './components/comment-with-attachments-dialog/comment-with-attachments-dialog.component';
import { ErrorMessageComponent } from './components/error-message/error-message.component';
import { FlyoutComponent } from './components/flyout/flyout.component';
import { SharedOffersGridComponent } from './components/offers-grid/offers-grid.component';
import { OpenTicketsComponent } from './components/open-tickets/open-tickets.component';
import { RatingNameComponent } from './components/rating-name/rating-name.component';
import { RichEditorDialogComponent } from './components/rich-editor-dialog/rich-editor-dialog-component';
import { RichToolTipComponent } from './components/rich-tooltip.component';
import { TimepickerFreeTextComponent } from './components/timepicker-free-text/timepicker-free-text.component';
import { UploadAttachmentsComponent } from './components/upload-attachments/upload-attachments.component';
import { DetailToolbarComponent } from './detail-toolbar.component';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { ClickStopPropagationDirective } from './directives/click-stop-propagation.directive';
import { ConnectAsyncFormDirective } from './directives/connect-async-form.directive';
import { NotIfLDFeatureDirective } from './directives/not-if-ld-feature.directive';
import { PointTwoFiveDirective } from './directives/point-two-five.directive';
import { RepeatElementDirective } from './directives/repeat-element.directive';
import { TypeaheadDirective } from './directives/typeahead.directive';
import { VersionCheckerDirective } from './directives/version-checker.directive';
import { JobFulfillmentTypeEffects } from './effects/job-fulfillment-type.effects';
import { StatesEffects } from './effects/states.effects';
import { GridFacilityVaccineFilterPopupComponent } from './grid/filters/grid-facility-vaccine-filter-popup.component';
import { SharedNotesComponent } from './offers-shared/notes-display/notes-display.component';
import { SharedOfferAttachmentsComponent } from './offers-shared/offer-attachments/offer-attachments.component';
import { SharedOfferCreateSubmittalComponent } from './offers-shared/offer-create-submittal/offer-create-submittal.component';
import { SharedOfferCreateComponent } from './offers-shared/offer-create/offer-create.component';
import { SharedOfferDetailsComponent } from './offers-shared/offer-details/offer-details.component';
import { OfferReasonDialogComponent } from './offers-shared/offer-dialogs/offer-reason-dialog/offer-reason-dialog.component';
import { ByteConvertPipe } from './pipes/byte-convert.pipe';
import { DateMessagePipe } from './pipes/date-message.pipe';
import { ReleaseDayPipe } from './pipes/days-to-release-pipe';
import { SelectedCandidateRequiredLabSelectionPipe } from './pipes/filter-lab-selection.pipe';
import { FilterPipe } from './pipes/filter-text.pipe';
import { OnlyOneSelectedPipe } from './pipes/only-one-selected.pipe';
import { PhonePipe } from './pipes/phone.pipe';

import { GridModule } from '@progress/kendo-angular-grid';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { BannerComponent } from 'src/app/shared/banner.component';
import { NovaAutoCompleteComponent } from 'src/app/shared/components/autocomplete/autocomplete.component';
import { JobRatingComponent } from 'src/app/shared/components/job-rating.component';
import { NeedsApprovalAlertComponent } from 'src/app/shared/components/needs-approval-alert/needs-approval-alert.component';
import { RichEditorComponent } from 'src/app/shared/components/rich-editor/rich-editor';
import { SidePanelComponent } from 'src/app/shared/components/side-panel/side-panel.component';
import { SubmittalMatchBreakdownReportIconComponent } from 'src/app/shared/components/submittal-match-breakdown/submittal-match-breakdown-report-icon/submittal-match-breakdown-report-icon.component';
import { IfLDFeatureDirective } from 'src/app/shared/directives/if-ld-feature.directive';
import { GridFilterPopupComponent } from 'src/app/shared/grid/filters/base-filter-popup.component';
import { GridAssignedToIdFilterComponent } from 'src/app/shared/grid/filters/grid-assigned-to-id-filter.component';
import { GridOptionListFilterPopupComponent } from 'src/app/shared/grid/filters/grid-option-list-filter-popup.component';
import { GridOptionListFilterComponent } from 'src/app/shared/grid/filters/grid-option-list-filter.component';
import { GridColumnDirective } from 'src/app/shared/grid/grid-column.directive';
import { GridComponent } from 'src/app/shared/grid/grid.component';
import { IconTriggerComponent } from 'src/app/shared/icon-trigger-components/icon-trigger/icon-trigger.component';
import { InfoDialogComponent } from 'src/app/shared/icon-trigger-components/info-dialog/info-dialog.component';
import { ArrayContainsPipe } from 'src/app/shared/pipes/array-contains.pipe';
import { ArrayFilterPipe } from 'src/app/shared/pipes/array-filter.pipe';
import { ArrayFindPipe } from 'src/app/shared/pipes/array-find.pipe';
import { DateByTimeZonePipe } from 'src/app/shared/pipes/date-by-timezone.pipe';
import { LenientDatePipe } from 'src/app/shared/pipes/lenient-date.pipe';
import { NullDashPipe } from 'src/app/shared/pipes/null-dash.pipe';
import { SanitizerPipe } from 'src/app/shared/pipes/sanitizer.pipe';
import { ExtractCertificationTitlesPipe } from '../recruiting/jobs/details/components/tabs/job-details/job-section/pipes/extract-certification-titles.pipe';
import { JoinCommaPipe } from '../recruiting/jobs/details/components/tabs/job-details/job-section/pipes/join-comma.pipe';
import { TranslateExperiencePipe } from '../recruiting/jobs/details/components/tabs/job-details/job-section/pipes/translate-experience.pipe';
import { RecruiterQualifiedCandidatesGridComponent } from '../recruiting/jobs/recruiter-qualified-candidates/recruiter-qualified-candidates-grid.component';
import { LocumsMarginCalculatorInitializerService } from '../recruiting/locums-margin-calculator/services/locums-margin-calculator-initializer.service';
import { CustomFieldViewerComponent } from './components/custom-fields/custom-field-viewer/custom-field-viewer.component';
import { JobQualificationsEffects } from './components/job-qualifications/effects/job-qualifications.effects';
import { JobQualificationsCompareComponent } from './components/job-qualifications/job-qualifications-compare/job-qualifications-compare.component';
import { JobUnitRequirementsComponent } from './components/job-unit-requirements/job-unit-requirements.component';
import { MentionListComponent } from './components/mention-list/mention-list.component';
import { MultiLineCustomTooltipComponent } from './components/multi-line-custom-tooltip/multi-line-custom-tooltip.component';
import { NgxAngularQueryBuilderModule } from './components/ngx-angular-query-builder/ngx-angular-query-builder.module';
import { NpiTextboxComponent } from './components/npi-textbox/npi-textbox.component';
import { WfdPriorityReturnerChipsViewComponent } from './components/wfd-priority-returner-chips/priority-returner-view/priority-returner-view.component';
import { EditableDirective } from './directives/editable.directive';
import { MentionDirective } from './directives/mention.directive';
import { MultiLineCustomTooltipDirective } from './directives/multi-line-custom-tooltip.directive';
import { OnlyNumberDirective } from './directives/only-numbers.directive';
import { BooleanPipe } from './pipes/boolean.pipe';
import { BusySignalPipe } from './pipes/busy-signal.pipe';
import { CalendarDateChunkPipe } from './pipes/calendar-date-chunk.pipe';
import { NoteWithFacContactLinksPipe } from './pipes/note-with-fac-contact-links.pipe';
import { NullNaPipe } from './pipes/null-na.pipe';
import { ReleaseTooltipPipe } from './pipes/release-tooltip.pipe';
import { ReplacePipe } from './pipes/replace-string.pipe';
import { StaffingRatioPipe } from './pipes/staffing-ratio.pipe';
import { StringToTimePipe } from './pipes/string-to-time.pipe';
import { SubmittalStatusFilterPipe } from './pipes/submittal-status-filter.pipe';
import { SubmittalStatusReasonFilterPipe } from './pipes/submittal-status-reason-filter.pipe';
import { TeamEntryTextPipe } from './pipes/team-entry-text.pipe';
import { UtcToPacificTimePipe } from './pipes/utc-to-pacific-time.pipe';
import { ValueByIdPipe } from './pipes/value-by-id.pipe';
import { RedirectComponent } from './redirect.component';
import { RtoAlertIconComponent } from './rto-alerts/rto-alert-icon/rto-alert-icon';
import { RtoWarningMessageComponent } from './rto-alerts/rto-warning-message/rto-warning-message';

const ANGULAR_MODULES = [CommonModule, FormsModule, ReactiveFormsModule];
// Separate initializer function for Locums Margin Calculator feature
const initLocumsMarginCalculator = () => () => {
    return Promise.resolve();
};

const ANGULAR_MATERIAL_MODULES = [
    MatAutocompleteModule,
    MatCardModule,
    MatChipsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatLegacyProgressBarModule,
    MatLegacyProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatStepperModule,
    MatTabsModule,
    MatToolbarModule,
    MatTableModule,
    MatTooltipModule,
    ScrollingModule,
    MatGridListModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
    MatTreeModule,
    MatBadgeModule
];

const KENDO_MODULES = [
    ButtonModule,
    PopupModule,
    TooltipModule,
    DialogModule,
    DropDownsModule,
    DateInputsModule,
    InputsModule,
    ButtonsModule,
    TimePickerModule,
    EditorModule,
    ToolBarModule,
    SchedulerModule,
    GridModule
];

const DIRECTIVES = [
    ClickStopPropagationDirective,
    TypeaheadDirective,
    ClickOutsideDirective,
    EventMetricsDirective,
    MatInputCommifiedDirective,
    VersionCheckerDirective,
    RepeatElementDirective,
    NotIfLDFeatureDirective,
    ConnectAsyncFormDirective,
    PointTwoFiveDirective,
    OnlyNumberDirective,
    MultiLineCustomTooltipDirective,
    MentionDirective,
    EditableDirective
];

/**
 * @deprecated - Use standalone components, directives, and pipes instead. Do not add new items to this module.
 */
@NgModule({
    imports: [
        ...ANGULAR_MODULES,
        ...ANGULAR_MATERIAL_MODULES,
        ...KENDO_MODULES,
        // 3rd party deps
        FileUploadModule,
        OverlayModule,
        // NgxPowerBiModule,
        RouterModule,
        NgxAngularQueryBuilderModule,
        // Custom shared modules
        IfLDFeatureDirective,
        NgxMaskDirective,
        NgxMaskPipe,
        NullDashPipe,
        StaffingRatioPipe,
        SanitizerPipe,
        LenientDatePipe,
        DateByTimeZonePipe,
        BannerComponent,
        ArrayContainsPipe,
        ByteConvertPipe,
        ArrayFilterPipe,
        ArrayFindPipe,
        BooleanPipe,
        NullNaPipe,
        BusySignalPipe,
        ...DIRECTIVES,
        // standalone pipes
        FilterPipe,
        ValueByIdPipe,
        GroupByPipe,
        SafedomPipe,
        ReplacePipe,
        LinkifyPipe,
        OrderByPipe,
        PayRangeTextPipe,
        NoteWithFacContactLinksPipe,
        EmptyArrayPipe,
        TeamEntryTextPipe,
        ReleaseTooltipPipe,
        DateMessagePipe,
        TextFormatPipe,
        OnlyOneSelectedPipe,
        JoinTextPipe,
        AssignmentLevelTypePipe,
        SubmittalStatusFilterPipe,
        SubmittalStatusReasonFilterPipe,
        PhonePipe,
        StringToTimePipe,
        ReleaseDayPipe,
        SelectedCandidateRequiredLabSelectionPipe,
        UtcToPacificTimePipe,
        RecruiterQualifiedCandidatesGridComponent,
        ExtractCertificationTitlesPipe,
        TranslateExperiencePipe,
        JoinCommaPipe,
        CalendarDateChunkPipe,
        IsEmptyPipe,
        // standalone components
        InfoDialogComponent,
        IconTriggerComponent,
        DateDialogComponent,
        DropDownDialogComponent,
        MessageDialogComponent,
        MoreOptionsMenuComponent,
        NoteDialogComponent,
        CardComponent,
        RedirectComponent,
        RichEditorDialogComponent,
        ImportJobRequirementsComponent,
        PaginationComponent,
        SpinnerComponent,
        ExpansionPanelComponent,
        GenericDialogComponent,
        TimepickerFreeTextComponent,
        CreateTicketMenuComponent,
        CalendarComponent,
        OpenTicketsComponent,
        SharedOfferCreateComponent,
        SharedOfferCreateSubmittalComponent,
        SharedOfferDetailsComponent,
        SharedNotesComponent,
        RtoWarningMessageComponent,
        NeedsApprovalAlertComponent,
        SharedOfferAttachmentsComponent,
        NovaAutoCompleteComponent,
        RtoAlertIconComponent,
        AutocompleteMultiselectComponent,
        RatingNameComponent,
        NovaAutoCompleteComponent,
        ErrorMessageComponent,
        DetailToolbarComponent,
        JobRatingComponent,
        RecruiterBonusComponent,
        JobQualificationsCompareComponent,
        RichToolTipComponent,
        FollowupPickerComponent,
        FollowupPickerPerdiemComponent,
        AddPerDiemFollowupPickerComponent,
        PerdiemRecruiterResourcesComponent,
        AutocompleteSelectAllMultiselectComponent,
        BillingRatesComponent,
        NeedsApprovalAlertComponent,
        SubmittalMatchBreakdownReportIconComponent,
        FlyoutComponent,
        OpenTicketsComponent,
        EmailContainer,
        NpiTextboxComponent,
        LeadScoreProfileRatingComponent,
        V1NotesComponent,
        RichEditorComponent,
        SearchTemplateComponent,
        TemplateFolderListComponent,
        TemplateGridComponent,
        TemplateMappingFlyOutComponent,
        FlyoutComponent,
        OpenTicketsComponent,
        AccessControlInformationViewEditComponent,
        VaccinePreviewComponent,
        JobUnitRequirementsComponent,
        MultiLineCustomTooltipComponent,
        JobFulfillmentTypeSelectorConfirmationComponent,

        GridComponent,
        GridColumnDirective,
        GridFilterPopupComponent,
        GridOptionListFilterPopupComponent,
        GridOptionListFilterComponent,
        GridAssignedToIdFilterComponent,
        WfdPriorityReturnerChipsViewComponent,
        GridFacilityVaccineFilterPopupComponent,
        CustomFieldViewerComponent,
        KendoMultipleDatePickerComponent,
        SidePanelComponent,
        OfferReasonDialogComponent,
        AtmButtonComponent,
        BillingInvoicingComponent,
        MentionListComponent,
        UploadAttachmentsComponent,
        BillingProfileComponent,
        BillingInfoComponent,
        WizardStepComponent,
        WizardComponent,
        SharedOffersGridComponent,
        CommentWithAttachmentsDialogComponent,
        JobFulfillmentTypeSelectorComponent
    ],
    exports: [
        RichEditorComponent,
        ...ANGULAR_MODULES,
        ...ANGULAR_MATERIAL_MODULES,
        ...KENDO_MODULES,
        // 3rd party deps
        FileUploadModule,
        // NgxPowerBiModule,
        ...DIRECTIVES,
        RouterModule,
        NgxMaskDirective,
        NgxMaskPipe,

        IfLDFeatureDirective,

        // Standalone components
        InfoDialogComponent,
        IconTriggerComponent,
        BannerComponent,
        ErrorMessageComponent,
        DetailToolbarComponent,
        RecruiterBonusComponent,
        JobRatingComponent,
        RichToolTipComponent,
        DateDialogComponent,
        DropDownDialogComponent,
        MessageDialogComponent,
        MoreOptionsMenuComponent,
        NoteDialogComponent,
        CardComponent,
        RedirectComponent,
        RichEditorDialogComponent,
        ImportJobRequirementsComponent,
        PaginationComponent,
        SpinnerComponent,
        ExpansionPanelComponent,
        GenericDialogComponent,
        TimepickerFreeTextComponent,
        DateDialogComponent,
        DropDownDialogComponent,
        MessageDialogComponent,
        MoreOptionsMenuComponent,
        NoteDialogComponent,
        CardComponent,
        RedirectComponent,
        RichEditorDialogComponent,
        ImportJobRequirementsComponent,
        PaginationComponent,
        SpinnerComponent,
        ExpansionPanelComponent,
        GenericDialogComponent,
        TimepickerFreeTextComponent,
        CreateTicketMenuComponent,
        CalendarComponent,
        OpenTicketsComponent,
        SharedOfferCreateComponent,
        SharedOfferCreateSubmittalComponent,
        SharedOfferDetailsComponent,
        SharedNotesComponent,
        RtoWarningMessageComponent,
        NeedsApprovalAlertComponent,
        RtoAlertIconComponent,
        AutocompleteMultiselectComponent,
        RatingNameComponent,
        ErrorMessageComponent,
        DetailToolbarComponent,
        JobRatingComponent,
        RecruiterBonusComponent,
        JobQualificationsCompareComponent,
        RichToolTipComponent,
        FollowupPickerComponent,
        FollowupPickerPerdiemComponent,
        AddPerDiemFollowupPickerComponent,
        PerdiemRecruiterResourcesComponent,
        AutocompleteSelectAllMultiselectComponent,
        BillingRatesComponent,
        NeedsApprovalAlertComponent,
        SubmittalMatchBreakdownReportIconComponent,
        FlyoutComponent,
        OpenTicketsComponent,
        EmailContainer,
        NpiTextboxComponent,
        LeadScoreProfileRatingComponent,
        V1NotesComponent,
        GridComponent,
        GridColumnDirective,
        GridFilterPopupComponent,
        GridOptionListFilterPopupComponent,
        GridOptionListFilterComponent,
        GridAssignedToIdFilterComponent,
        SearchTemplateComponent,
        TemplateFolderListComponent,
        TemplateGridComponent,
        TemplateMappingFlyOutComponent,
        GridFacilityVaccineFilterPopupComponent,
        VaccinePreviewComponent,
        SidePanelComponent,
        OfferReasonDialogComponent,
        AtmButtonComponent,
        BillingInvoicingComponent,
        MentionListComponent,
        UploadAttachmentsComponent,
        BillingProfileComponent,
        BillingInfoComponent,
        WizardStepComponent,
        WizardComponent,
        SharedOffersGridComponent,
        CommentWithAttachmentsDialogComponent,
        JobFulfillmentTypeSelectorComponent,
        MultiLineCustomTooltipComponent,
        JobFulfillmentTypeSelectorConfirmationComponent,

        // standalone pipes
        FilterPipe,
        ValueByIdPipe,
        GroupByPipe,
        SafedomPipe,
        ReplacePipe,
        LinkifyPipe,
        OrderByPipe,
        PayRangeTextPipe,
        NoteWithFacContactLinksPipe,
        EmptyArrayPipe,
        TeamEntryTextPipe,
        ReleaseTooltipPipe,
        DateMessagePipe,
        TextFormatPipe,
        OnlyOneSelectedPipe,
        JoinTextPipe,
        AssignmentLevelTypePipe,
        SubmittalStatusFilterPipe,
        SubmittalStatusReasonFilterPipe,
        PhonePipe,
        StringToTimePipe,
        ReleaseDayPipe,
        SelectedCandidateRequiredLabSelectionPipe,
        UtcToPacificTimePipe,
        ExtractCertificationTitlesPipe,
        TranslateExperiencePipe,
        JoinCommaPipe,
        CalendarDateChunkPipe,
        IsEmptyPipe,
        ArrayContainsPipe,
        ByteConvertPipe,
        ArrayFilterPipe,
        ArrayFindPipe,
        BooleanPipe,
        NullNaPipe,
        BusySignalPipe,
        NullDashPipe,
        SanitizerPipe,
        LenientDatePipe,
        DateByTimeZonePipe
    ],
    providers: [
        provideNgxMask(),
        { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
        SubmittalStatusReasonFilterPipe,
        {
            provide: APP_INITIALIZER,
            useFactory: initLocumsMarginCalculator,
            multi: true,
            deps: [LocumsMarginCalculatorInitializerService]
        },
        provideState('shared', sharedReducers),
        provideEffects([
            ConfirmationDialogEffects,
            ToasterEffects,
            ContractAssignmentsEffects,
            StatesEffects,
            JobFulfillmentTypeEffects,
            JobQualificationsEffects
        ])
    ]
})
export class SharedModule {}
